'use client'

import { useEffect, type ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import { ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useSession } from '@/hooks/useSession'

/**
 * Client-side route protection for the panel.
 *
 * A confirmed guest is sent to /login; a signed-in NON-admin gets the not-authorised screen and a
 * way out. The UI is never the security boundary — every admin operation is `allow.group('admins')`
 * in the schema and re-checked in its handler; this only keeps the panel from rendering skeletons
 * against queries the server would refuse anyway.
 */
export function AdminGuard({ children }: { children: ReactNode }) {
    const router = useRouter()
    const { status, isAdmin } = useSession()

    useEffect(() => {
        if (status === 'guest') { router.replace('/login') }
    }, [status, router])

    if (status === 'unknown') {
        return (
            <div className="grid min-h-dvh place-items-center bg-background" role="status" aria-label="Checking your session">
                <div className="w-full max-w-xs space-y-3">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                </div>
            </div>
        )
    }
    if (status === 'guest') { return null }
    if (!isAdmin) { return <NotAuthorised /> }
    return <>{children}</>
}

/** A signed-in account outside the admins group. Names the account, so a wrong-account sign-in is obvious. */
function NotAuthorised() {
    const router = useRouter()
    const { email, signOut, isSigningOut } = useSession()
    return (
        <div className="grid min-h-dvh place-items-center bg-background p-6 text-center">
            <div className="max-w-sm">
                <span className="mx-auto mb-5 grid size-14 place-items-center rounded-xl bg-destructive/10">
                    <ShieldCheck className="size-7 text-destructive" aria-hidden="true" />
                </span>
                <h1 className="text-2xl font-semibold tracking-tight">Not authorised</h1>
                <p className="mt-2 leading-relaxed text-muted-foreground">
                    {email ?? 'This account'} is signed in but is not in the admins group, so the panel stays closed.
                    Membership is granted by hand — see the README.
                </p>
                <Button variant="outline" className="mt-6" pending={isSigningOut} onClick={async () => { await signOut(); router.replace('/login') }}>Sign out</Button>
            </div>
        </div>
    )
}
